/**
 * ECH TEMPLATE ASSEMBLY — port of the monolith's template layer
 * (index.html L8493–8859 + `__echRebuildKeepEdits` L11748–11775).
 *
 * WHY THIS FILE EXISTS (audit row 23 — the blocker this pass exists to close)
 * -------------------------------------------------------------------------
 * The previous pass made every one of Layne's SFMC blocks a real, selectable,
 * guardrailed block (`echRegistry.ts`) and made dynamic fill run over the whole
 * document (`echDyn.ts`). Neither was reachable from Jack's own path: the form
 * preselects a template, `StudioView` sees `tplHtml` and goes into
 * `TemplateEditor` mode, and the block canvas the entire ECH layer lives on never
 * appears — observed live in Chrome on 2026-08-19 as `.esmod` count 0.
 *
 * The missing piece is the thing that turns a TEMPLATE NAME into a SEQUENCE OF
 * BLOCKS. In the monolith that is `loadTpl()` → `echDoc(name)`:
 *
 *   template name ─echTplFor─▶ canonical name ─echSeq─▶ ordered placements
 *                                                    └─echDoc─▶ Studio doc
 *
 * TEMPLATES ARE DATA IN SFMC, NOT CODE HERE (2026-08-18, per Jack)
 * ----------------------------------------------------------------
 * Each template is ONE Content Builder block named `ECH_TPL_<Name>` in "Test -
 * Jack Blocks" whose only payload is a JSON header:
 *
 *     <!--@GUARDRAILS
 *     { "kind":"template", "template":"Coming Soon",
 *       "aliases":["Tease - Coming Soon"],
 *       "blocks":["ECH_Logo","ECH_ComingSoonImage",
 *                 {"block":"ECH_BlueHeader","set":{"sectionTitle":"Visit Us"}}] }
 *     @-->
 *
 * Consequences, which are the whole point of the exercise:
 *   • edit a CONTENT block  → every template that references it changes
 *   • edit a TEMPLATE block → only that template's order changes
 *   • add an `ECH_TPL_` block → a new template appears, with no release here
 *
 * So there is NO hard-coded block order in this file. `ECH_MAP` / `ECH_ALIAS` /
 * `ECH_TPLMETA` are built at runtime from those blocks by `echLoadTemplates()`.
 * `ECH_TPL_*` stays EXCLUDED from the block registry (`echRegistry.ts`) — it is a
 * template DEFINITION, not a placeable module.
 *
 * The object form of a `blocks[]` entry is what lets ONE block appear TWICE in a
 * template saying different things: Coming Soon uses `ECH_BlueHeader` at position
 * 6 ("Amenities") and again at position 8 ("Visit Us"). That is also exactly why
 * `echRebuildKeepEdits` has to match on content id PLUS occurrence index.
 */
import { FULL_REGISTRY } from "../email/blocks";
import { esc } from "../email/core";
import type { AnyBlockInstance } from "../email/document";
import { echByName, echMeta, echTypes } from "./echRegistry";
import { EXTRA_COLLS, rowApplyExtraColls } from "./echRows";
import { parseGrJson, type SfmcBlock } from "./sfmcBlocks";

/* ── THE ID / TWIN TABLE (monolith `ECH_BLOCKS`, L8462–8491) ─────────────────
   Source: ECH_Template_Block_Map.xlsx (Layne Cutler, 2026-08-17).

   WHY THIS IS A FALLBACK, NOT THE DESIGN. The real home for a ContentID is the
   block we actually pulled from `/sfmc-blocks` — `echByName()` is asked first and
   this table is only a last-resort lookup for a block that was never pulled.

   ⛔ THE `twin` IS NO LONGER RENDERED (2026-08-20, per Jack: *"make sure this doesn't
   happen again — if it isn't there it shouldn't show"*).

   It used to be the Studio block that APPROXIMATED an SFMC block when the real one
   was missing, on the theory that `__ech.approx` marked it as not-Layne's. That
   theory failed in practice: the canvas draws a twin identically to a real block, so
   a substitution is invisible. Jack found `ECH_BlueHeader` — a navy `#1B2A58` section
   bar with one `sectionTitle` field — rendering as our `eyebrow` block's default,
   gold letter-spaced "COMING SOON" (measured: `letter-spacing:.18em` appears in
   `email/registry.ts:113` and in NONE of her 29 blocks). Content we invented was
   sitting in an email preview looking exactly like hers.

   `echDoc` now resolves the real block or the NAMED PLACEHOLDER, nothing else. The
   column is kept only because it records which Studio block was once considered
   equivalent, and because deleting the field would touch every entry for no gain —
   but nothing reads it to render. Do not wire it back in. If a block is missing the
   honest answer is to say which one, not to draw a lookalike. */
export const ECH_BLOCKS: Record<string, { id: string; twin: string | null }> = {
  ECH_Logo: { id: "1028542", twin: "logo" },
  ECH_ComingSoonImage: { id: "1028546", twin: "hero" },
  ECH_FindDreamHomeImage: { id: "1028549", twin: "hero" },
  ECH_JustLaunchedImage: { id: "1028551", twin: "hero" },
  ECH_AvailableNowImage: { id: "1028553", twin: "hero" },
  ECH_1CTA: { id: "1028556", twin: "cta" },
  ECH_2CTA: { id: "1028557", twin: "cta" },
  ECH_CopyBlock: { id: "1028580", twin: "body" },
  ECH_ListingTable: { id: "1028584", twin: "listing" },
  ECH_4Amenities: { id: "1028589", twin: "amenities" },
  ECH_3Amenities: { id: "1028590", twin: "amenities" },
  ECH_Featured2CTA: { id: "1028591", twin: "community" },
  ECH_Featured1CTA: { id: "1028592", twin: "community" },
  ECH_ContactUsDefault: { id: "1028595", twin: "isc" },
  ECH_SocialIcons: { id: "1028596", twin: "footer" },
  ECH_Disclaimer: { id: "1028601", twin: null },
  ECH_DisclaimerMortgage: { id: "1028603", twin: null },
  ECH_DisclaimerMortgageTitle: { id: "1028604", twin: null },
  ECH_DisclaimerMortgageTitleLIA: { id: "1028605", twin: null },
  ECH_GetDirections: { id: "1028614", twin: "map" },
  ECH_TableHeader: { id: "1028622", twin: "eyebrow" },
  ECH_DividerLine: { id: "1028623", twin: "divider" },
  ECH_GOInvite2CTA: { id: "1028824", twin: "event" },
  ECH_GOInvite1CTA: { id: "1028827", twin: "event" },
  ECH_BlueHeader: { id: "1028829", twin: "eyebrow" },
  ECH_GOTY1CTA: { id: "1028831", twin: "event" },
  ECH_GOTY2CTA: { id: "1041008", twin: "event" },
  ECH_VIPImage: { id: "1028840", twin: "hero" },
  ECH_MainImage: { id: "1028870", twin: "image" },
};

/**
 * THE PLACEHOLDER BLOCK (monolith `M.echblock`, L8067–8073).
 *
 * Registered here rather than in `email/registry-extended.ts` on purpose: it is
 * not part of the parity-gated production set and nothing must be able to reach
 * it from the palette. `hidden:true` matches the monolith — it only ever arrives
 * via `echDoc()`, so the canvas can show the real block SEQUENCE without
 * pretending to render artwork that lives in Content Builder.
 */
export const ECH_PLACEHOLDER = "echblock";
FULL_REGISTRY[ECH_PLACEHOLDER] = {
  /* the monolith's label is "SFMC block"; every registry label is title-cased at
     load (blocks.ts `__tc`), which this registration happens after — so the
     already-title-cased form is what keeps the DISPLAYED tag identical */
  label: "Missing Block",
  kind: "module",
  grp: "Structure",
  hidden: true,
  def: () => ({ name: "SFMC block", cid: "" }),
  /**
   * A CENTRED RED EYEBROW, AND NOTHING ELSE (2026-08-20, per Jack: *"just mark an
   * eyebrow in red stating missing from sfmc… make sure this kind of block is centred
   * and stating locked and not editable"*).
   *
   * The first version was a left-aligned dashed panel with a sand fill — furniture, and
   * it read as a design element rather than a problem. One centred red line matches the
   * eyebrow rhythm the templates already use, is unmistakably not artwork, and takes one
   * row instead of a card. The ContentID rides along quietly so eMarketing can be given
   * something precise to look up.
   *
   * Still dropped from the export by `renderRows` — this is a note to the requester, not
   * content, and red debug copy must never reach a customer.
   */
  render: (d: Record<string, unknown>) =>
    '<tr><td align="center" style="padding:20px 40px;font-family:Arial,Helvetica,sans-serif">' +
    '<div style="font-size:12px;font-weight:bold;letter-spacing:.18em;text-transform:uppercase;color:#b3261e">Missing from SFMC</div>' +
    '<div style="font-size:12px;font-weight:bold;color:#b3261e;padding-top:5px">' +
    esc(d.name || "SFMC block") +
    (d.cid ? '<span style="font-weight:500"> &middot; ContentID ' + esc(d.cid) + "</span>" : "") +
    "</div></td></tr>",
  /* NO EDITABLE FIELDS. `name` and `cid` were text inputs, which invited a requester to
     type over the one thing that identifies what is missing — and made the panel look
     like the block could be fixed here. It can't: the fix is in Content Builder. An
     empty field list is what makes the Inspector render its locked card instead. */
  fields: [],
};

/** one entry of a template's `blocks[]`, normalised */
export interface EchEntry {
  block: string;
  /** per-instance field presets, e.g. `{sectionTitle:"Visit Us"}` */
  set: Record<string, unknown> | null;
}

/**
 * ONE CROSS-BLOCK RULE off a template definition block.
 *
 * A per-block `maxCount` cannot express "one main image across ECH_MainImage AND
 * ECH_MainImage600" or "never mix the two featured variants" — those span blocks, so
 * they are declared once per template instead. Read verbatim off the live headers
 * (all seven `ECH_TPL_*`, 2026-08-26).
 *
 * Only `maxCombinedCount` and `mutuallyExclusive` are enforced by the CAP check.
 * `lockedPosition`, `mustImmediatelyFollow` and `mustImmediatelyPrecede` are ordering
 * rules and live in `echPlacement.ts` (2026-08-27) — a cap check was the wrong place for
 * them, which is why they sat carried-but-unread until Layne reported it. `interchangeable`
 * is a substitution rule and is still carried rather than enforced.
 */
export interface EchTemplateRule {
  id?: string;
  type?: string;
  /** the SFMC block names the rule spans */
  applies?: string[];
  /**
   * The OBJECT form of `applies`, used only by `lockedPosition`: `[{block, position}]`
   * (2026-08-27). It is kept separate from `applies` on purpose — `applies` was typed
   * `string[]` and `String({block:…})` turned every entry into "[object Object]", so the
   * rule was carried but unreadable, which is exactly how a declared key ends up ignored.
   * `applies` still holds the string form for every other rule type, so `blockCaps` is
   * untouched.
   */
  appliesAt?: { block: string; position: "first" | "last" | number }[];
  /** what `mustImmediatelyFollow` / `mustImmediatelyPrecede` must sit next to. Accepts
   *  the live `firstOf:A|B` form as well as a plain block name. */
  targets?: string[];
  /** the block this one is dragged WITH, as a unit (`movesWith`) */
  movesWith?: string;
  max?: number;
  /**
   * The rule's own note. Carried, NOT shown: these are written for whoever maintains the
   * blocks ("Each block also declares maxCount: 1 in its own header"), and a toast on a
   * built surface is not the place for spec prose. The placement check writes its own
   * one-line refusal.
   */
  message?: string;
  /** `lockedFieldValue`'s target and value — carried, not acted on (see EchRule below) */
  field?: string;
  value?: unknown;
  scope?: string;
}

/**
 * ONE RULE TYPE, NOT TWO (reconciled 2026-08-26).
 *
 * This change was authored 2026-08-22 and declared its own `EchRule`. It sat unmerged
 * while #41 shipped `EchTemplateRule` above — the same shape, read the same way, for a
 * different caller: #41 caps PLACEMENT (the duplicate button and the palette), this one
 * caps the wizard's featured SECTION. Landing both would leave this file with two
 * models of one Marketing Cloud array, which is the duplication Jack's ruling was about
 * ("gardrail about 5 comunites should all be in the sfmc garudrails never hard code in
 * dshboar") just moved up a level.
 *
 * So `EchRule` is an ALIAS. The three extra keys the 08-22 version carried — `field`,
 * `value` and `scope`, for the `lockedFieldValue` rule nothing acts on yet — are folded
 * into `EchTemplateRule` rather than dropped.
 */
export type EchRule = EchTemplateRule;

/** what a template definition block declares */

export interface EchTemplateDef {
  kind?: string;
  template?: string;
  aliases?: string[];
  blocks?: unknown[];
  review?: string[];
  source?: string;
  rules?: unknown[];
  /** the template's stage (Tease / Launch / Sustain / Last Chance / Events), when declared */
  stage?: string;
  /** the SFMC name of the shell this template renders in (e.g. `ECH_V2_Shell`);
   *  absent = the default `ECH_Blank_Template` — see templateShellName */
  shell?: string;
}

/** provenance of one loaded template */
export interface EchTplMeta {
  /** the definition block's SFMC name, e.g. `ECH_TPL_ComingSoon` */
  defBlock: string;
  /** the definition block's ContentID */
  defId: string;
  /** open questions Layne/Jack left on the mapping */
  review: string[];
  source: string;
  /** the template's cross-block rules, normalised (never null — [] when none) */
  rules: EchTemplateRule[];
  /** the declared `stage`, else the stage its aliases carry ("Tease - Coming Soon"); "" when neither */
  stage: string;
  /** the shell block this template names ("" = the default shell) */
  shell: string;
}

/** one resolved placement in a template */
export interface EchPlacementItem {
  /** the SFMC block name */
  block: string;
  /** the SFMC ContentID ("" when we have never seen the block) */
  id: string;
  /** 1-based position */
  pos: number;
  /** the approximating Studio block, when one is known */
  twin: string | null;
  /** the registry key when the placement came from a registered block */
  mkey: string | null;
  set: Record<string, unknown> | null;
  src: "sfmc" | "sfmc-template-block";
}

/* CLEARED IN PLACE, NEVER REASSIGNED (monolith L8527–8531). Reassigning
   `ECH_MAP = {}` orphans every reference already handed out — in the monolith
   `window.__ech.map` went permanently empty while the Studio itself worked fine,
   which reads as "no templates loaded" in any diagnostic. */
export const ECH_MAP: Record<string, EchEntry[]> = {};
export const ECH_ALIAS: Record<string, string> = {};
export const ECH_TPLMETA: Record<string, EchTplMeta> = {};
/** each template's raw definition, so a reader of a NEW key (M2's `palette`, `editableBetween`,
 *  `familyDefault`) needs no change here — see services/m2Rules.ts (2026-10-07) */
export const ECH_TPLDEF: Record<string, Record<string, unknown>> = {};

/** bumped on every reload so React can re-derive; the monolith has no analogue
 *  because it re-renders imperatively. */
let TPL_VERSION = 0;
export function echTplVersion(): number { return TPL_VERSION; }

/**
 * Normalise one `blocks[]` entry to `{block, set}` (monolith `echEntry` L8516).
 * A bare string is the common case; the object form presets fields.
 */
export function echEntry(e: unknown): EchEntry | null {
  if (typeof e === "string") return { block: e, set: null };
  if (e && typeof e === "object") {
    const o = e as { block?: unknown; set?: unknown };
    if (o.block) return { block: String(o.block), set: (o.set as Record<string, unknown>) || null };
  }
  return null;
}

/** the template definition a block declares, or null (worker-parsed first, then
 *  the block's own comment — the file:// cache path carries the comment only) */
function tplDefOf(b: SfmcBlock): EchTemplateDef | null {
  const g = (b.guardrails as unknown as EchTemplateDef) || (parseGrJson(b.html || "") as EchTemplateDef | null);
  return g && typeof g === "object" ? g : null;
}

/**
 * Rebuild the template registry from every `ECH_TPL_*` block in the payload
 * (monolith `echLoadTemplates` L8524–8546). Returns how many templates loaded.
 */
export function echLoadTemplates(blocks: SfmcBlock[]): number {
  let n = 0;
  try {
    const clr = (o: Record<string, unknown>) => Object.keys(o).forEach((k) => { delete o[k]; });
    clr(ECH_MAP); clr(ECH_ALIAS); clr(ECH_TPLMETA); clr(ECH_TPLDEF);
    for (const b of blocks || []) {
      const name = String(b?.name || "");
      if (!/^ECH_TPL_/i.test(name)) continue;
      const g = tplDefOf(b);
      if (!g || g.kind !== "template" || !g.template || !Array.isArray(g.blocks)) continue;
      const seq = g.blocks.map(echEntry).filter((x): x is EchEntry => !!x);
      if (!seq.length) continue;
      const tpl = String(g.template);
      ECH_MAP[tpl] = seq;
      ECH_TPLMETA[tpl] = {
        defBlock: name, defId: String(b.id || ""), review: g.review || [], source: g.source || "",
        rules: echRules(g.rules),
        stage: echStageOfDef(g),
        shell: typeof g.shell === "string" ? g.shell.trim() : "",
      };
      ECH_TPLDEF[tpl] = g as unknown as Record<string, unknown>;
      (g.aliases || []).forEach((a) => { if (a) ECH_ALIAS[String(a)] = tpl; });
      n++;
    }
  } catch { /* a malformed definition block must not stop the Studio opening */ }
  TPL_VERSION++;
  return n;
}

/**
 * Normalise a definition block's `rules` array. A malformed entry is DROPPED rather
 * than repaired — an invented `applies` list would cap blocks nobody asked to cap,
 * and a missing rule is a placement that goes through, which is the safer failure.
 */
function echRules(raw: unknown): EchTemplateRule[] {
  if (!Array.isArray(raw)) return [];
  const out: EchTemplateRule[] = [];
  for (const r of raw) {
    if (!r || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    /* `applies` arrives EITHER as block names or, for lockedPosition, as
       `{block, position}` objects (live headers, 2026-08-27). Split them: strings keep
       the old contract for every existing reader, objects go to `appliesAt`. */
    /* ── EITHER KEY NAME (2026-09-28, Layne's ASK-JACK-PIN-DELETE-GUARD) ─────────────
       Her headers author `applies`; she read `pinnedRules()` (which reads the NORMALISED
       `appliesAt`) and concluded the rule never fired, so she planned to rename the key.
       It did fire — this split is what feeds `appliesAt` — and a raw `appliesAt` key was
       IGNORED here, so the rename would have silently dropped every pin. Both spellings
       now read the same; when both are present they are merged. */
    const raws = [
      ...(Array.isArray(o.applies) ? o.applies : []),
      ...(Array.isArray(o.appliesAt) ? o.appliesAt : []),
    ];
    const applies = raws.filter((x) => typeof x === "string").map((x) => String(x)).filter(Boolean);
    const appliesAt = raws
      .filter((x): x is Record<string, unknown> => !!x && typeof x === "object" && !Array.isArray(x))
      /* a position of 0 is legal ("first" by index), so the test is against null/undefined
         and the empty string, never falsiness */
      .filter((x) => String(x.block ?? "") !== "" && x.position != null && x.position !== "")
      .map((x) => ({ block: String(x.block), position: x.position as "first" | "last" | number }));
    const targets = Array.isArray(o.targets) ? o.targets.map((x) => String(x)).filter(Boolean) : undefined;
    const max = typeof o.max === "number" && isFinite(o.max) ? o.max : undefined;
    out.push({
      id: o.id == null ? undefined : String(o.id),
      type: o.type == null ? undefined : String(o.type),
      applies,
      appliesAt: appliesAt.length ? appliesAt : undefined,
      targets,
      movesWith: o.movesWith == null ? undefined : String(o.movesWith),
      max,
      message: o.message == null ? undefined : String(o.message),
    });
  }
  return out;
}

/**
 * THE STAGE COMES FROM THE TEMPLATE ITSELF (2026-10-06, Layne: "could both come from the
 * template itself, with stage read from aliases (or a stage key)"). A declared `stage`
 * wins; otherwise the first alias whose prefix names a stage ("Tease - Coming Soon",
 * "Launch - We're Open", "Last Chance - …"). Returned in the app's stage vocabulary
 * (Tease / Launch / Sustain / LastChance / Events), "" when the template says nothing —
 * the caller then falls back to reading the name (dupJobs.tplStageOf).
 */
const STAGE_WORDS: [RegExp, string][] = [
  [/^\s*last\s*chance\b/i, "LastChance"],
  [/^\s*(fast\s*track\s*-\s*)?tease\b/i, "Tease"],
  [/^\s*(fast\s*track\s*-\s*)?launch\b/i, "Launch"],
  [/^\s*sustain\b/i, "Sustain"],
  [/^\s*events?\b/i, "Events"],
  [/^\s*urgency\b/i, "Urgency"],
];
export function stageWord(s: unknown): string {
  const t = String(s || "");
  for (const [re, st] of STAGE_WORDS) if (re.test(t)) return st;
  return "";
}
function echStageOfDef(g: EchTemplateDef): string {
  const declared = stageWord(g.stage);
  if (declared) return declared;
  for (const a of g.aliases || []) { const st = stageWord(a); if (st) return st; }
  return "";
}
/** the stage a loaded template declares (by name or alias), or "" */
export function echTplStage(name: string): string {
  const tpl = echTplFor(name);
  return (tpl && ECH_TPLMETA[tpl] && ECH_TPLMETA[tpl].stage) || "";
}
/** every loaded template's canonical name AND every alias — what a job or favorite can be built from */
export function echTemplateNamesAndAliases(): string[] {
  return [...Object.keys(ECH_MAP), ...Object.keys(ECH_ALIAS)];
}

/**
 * WHICH SHELL A TEMPLATE RENDERS IN (2026-10-06, Layne: "Option B — each template says
 * which shell it uses"). Read straight from the payload's `ECH_TPL_*` definitions, not
 * from ECH_TPLMETA, because `useEchShell` resolves the shell from a payload it has not
 * adopted (see that hook's READ, NEVER ADOPT note) — the registry can lag the pull.
 *
 * Matches the definition whose `template` or one of its `aliases` equals the name
 * (case-insensitive), then falls back to whatever `echTplFor` resolves the name to
 * (the form's stage-prefixed names). "" = no shell named — use the default.
 */
export function templateShellName(blocks: SfmcBlock[] | null | undefined, name: string): string {
  const want = String(name || "").trim().toLowerCase();
  if (!want || !blocks || !blocks.length) return "";
  try {
    const defs: EchTemplateDef[] = [];
    for (const b of blocks) {
      if (!/^ECH_TPL_/i.test(String(b?.name || ""))) continue;
      const g = tplDefOf(b);
      if (g && g.kind === "template" && g.template) defs.push(g);
    }
    const named = (g: EchTemplateDef) => (typeof g.shell === "string" ? g.shell.trim() : "");
    const hit = (n: string) => defs.find((g) =>
      String(g.template).toLowerCase() === n || (g.aliases || []).some((a) => String(a || "").toLowerCase() === n));
    const exact = hit(want);
    if (exact) return named(exact);
    const canon = echTplFor(name);
    const loose = canon ? hit(canon.toLowerCase()) : undefined;
    return loose ? named(loose) : "";
  } catch { return ""; }
}

/** every loaded template's canonical name */
export function echTemplateNames(): string[] { return Object.keys(ECH_MAP); }

/* ── THE "ECH - " RENAME (Layne, 2026-10-19) ───────────────────────────────────────────
   All ten templates were renamed to match Katy's Airtable names: "Coming Soon" became
   "ECH - Coming Soon", and so on. The old name stays in each template's `aliases`. The
   prefix is ignored when comparing, so the app works on either side of the rename in
   either library (_TEST and prod rename on different days): "ECH - Coming Soon" finds the
   old "Coming Soon", and an old saved draft's "Coming Soon" finds the renamed one. */
const tplKey = (s: string): string => String(s || "").trim().toLowerCase().replace(/^ech\s*-\s*/, "").trim();

/**
 * The loaded template a name means, by EXACT identity only — canonical name, alias,
 * case, or the "ECH - " prefix. Unlike `echTplFor` there is no substring guess, so it is
 * safe for rewriting a stored value (the form's template, which is what Airtable's
 * Email Nurture Template field receives). null when no loaded template is that name.
 */
export function echCanonicalName(name: string): string | null {
  const n = String(name || "").trim();
  if (!n) return null;
  if (ECH_MAP[n]) return n;
  if (ECH_ALIAS[n]) return ECH_ALIAS[n];
  const k = tplKey(n);
  if (!k) return null;
  for (const t of Object.keys(ECH_MAP)) if (tplKey(t) === k) return t;
  for (const a of Object.keys(ECH_ALIAS)) if (tplKey(a) === k) return ECH_ALIAS[a];
  return null;
}

/**
 * The cross-block rules for a template, by canonical name OR alias. [] for an
 * unknown template, which is also what a bespoke document gets — no template, no
 * template-level rules, and the per-block `maxCount` still applies.
 */
export function echTplRules(name: string): EchTemplateRule[] {
  const n = String(name || "");
  if (!n) return [];
  const tpl = ECH_MAP[n] ? n : ECH_ALIAS[n] || "";
  const meta = tpl ? ECH_TPLMETA[tpl] : null;
  return (meta && meta.rules) || [];
}

/**
 * Resolve any name the Studio might hold to an `ECH_MAP` key — exact, then alias,
 * then loose (monolith `echTplFor` L8790–8802).
 *
 * The loose pass is what makes the form's stage-prefixed names work without a
 * second table: the form's default is `Tease - Coming Soon`, which is a declared
 * alias, while `Sustain - Actively Selling` reaches `Available Now` the same way.
 */
export function echTplFor(name: string): string | null {
  if (!name) return null;
  const n = String(name).trim();
  if (ECH_MAP[n]) return n;
  if (ECH_ALIAS[n]) return ECH_ALIAS[n];
  const canon = echCanonicalName(n);
  if (canon) return canon;
  const low = n.toLowerCase();
  const keys = Object.keys(ECH_MAP);
  for (const k of keys) if (low.indexOf(k.toLowerCase()) > -1) return k;
  const ak = Object.keys(ECH_ALIAS);
  for (const a of ak) if (low.indexOf(a.toLowerCase()) > -1) return ECH_ALIAS[a];
  return null;
}

/**
 * SFMC-DECLARED PLACEMENTS WIN OVER THE TEMPLATE BLOCK (monolith `echSfmcSeq`
 * L8806–8820). A content block annotated `{"templates":{"Coming Soon":3}}` moves
 * itself, and nothing here needs a release.
 *
 * Reads the same two shapes `__grSync` stores (L13142/13148): the `templates`
 * MAP first — `template` + `order` is a scalar pair and cannot express the real
 * map, since ECH_MainImage is position 5 in five templates and 3 in two.
 *
 * Verified against the 2026-08-18 block cache: NO content block declares either
 * today, so this returns [] and `echSeq` falls through to the template block's
 * own order. It is ported because the path is live the moment Layne annotates one.
 */
export function echSfmcSeq(tpl: string): EchPlacementItem[] {
  const out: EchPlacementItem[] = [];
  try {
    for (const key of echTypes()) {
      const m = echMeta(key);
      const g = m && m.full;
      if (!m || !g) continue;
      let pos: number | null = null;
      if (g.templates && g.templates[tpl] != null) pos = Number(g.templates[tpl]);
      else if (g.template === tpl && g.order) pos = Number(g.order);
      if (pos == null || !isFinite(pos)) continue;
      out.push({ block: m.sfmc.name || key, id: String(m.sfmc.id), pos, twin: null, mkey: key, set: null, src: "sfmc" });
    }
  } catch { return []; }
  out.sort((a, b) => a.pos - b.pos);
  return out;
}

/** Ordered placements for a template, or null when the name isn't an ECH
 *  template (monolith `echSeq` L8823–8836). */
export function echSeq(name: string): EchPlacementItem[] | null {
  const tpl = echTplFor(name);
  if (!tpl) return null;
  const live = echSfmcSeq(tpl);
  if (live.length) return live;
  const list = ECH_MAP[tpl] || [];
  return list.map((e, i) => {
    const meta = ECH_BLOCKS[e.block] || { id: "", twin: null };
    /* the ContentID comes from the block we actually PULLED when we have it; the
       table above is only a last-resort lookup for a block we never saw */
    const pulled = echByName(e.block);
    return {
      block: e.block,
      id: String((pulled && pulled.sfmc.id) || meta.id || ""),
      pos: i + 1,
      twin: meta.twin || null,
      mkey: null,
      set: e.set || null,
      src: "sfmc-template-block" as const,
    };
  });
}

/**
 * Turn the placement list into a Studio document so the canvas shows the real
 * sequence (monolith `echDoc` L8839–8859).
 *
 * Resolution order per placement: the REAL SFMC block (registered as
 * `sfmc_<ContentID>`), then the approximate twin, then the labelled placeholder.
 * Once the blocks are reachable, NOTHING here is hard-coded template HTML — the
 * markup is Layne's.
 *
 * `mk` is the app's own block factory (App `makeBlock`), so an assembled block is
 * indistinguishable from one inserted by hand — same id scheme, same defaults.
 */
export function echDoc(name: string, mk: (type: string) => AnyBlockInstance): AnyBlockInstance[] | null {
  const seq = echSeq(name);
  if (!seq || !seq.length) return null;
  return seq.map((b) => {
    /* LAYNE'S BLOCK, OR A PLACEHOLDER THAT NAMES IT. NOTHING IN BETWEEN.
       (2026-08-20 — "if it isn't there it shouldn't show".)

       The chain used to fall through `b.mkey` and then `b.twin` to a Studio lookalike.
       Both are gone: a lookalike is indistinguishable from the real block on canvas, so
       the requester cannot tell our markup from Content Builder's, and the export would
       carry ours. The placeholder says which block is missing and renders no artwork —
       which is the only honest thing to draw when the real one has not arrived. */
    const real = "sfmc_" + b.id;
    let t = FULL_REGISTRY[real] ? real : ECH_PLACEHOLDER;
    let m: AnyBlockInstance;
    try { m = mk(t); } catch { m = mk(ECH_PLACEHOLDER); t = ECH_PLACEHOLDER; }
    if (t === ECH_PLACEHOLDER) { m.data.name = b.block; m.data.cid = b.id; }
    /* per-instance field presets from the template block, e.g.
       sectionTitle:"Visit Us" — this is what lets ONE block appear twice in a
       template saying different things */
    if (b.set) { try { Object.keys(b.set).forEach((k) => { m.data[k] = (b.set as Record<string, unknown>)[k]; }); } catch { /* noop */ } }
    const isReal = t === real;
    m.__ech = {
      name: b.block, id: b.id, pos: b.pos, src: b.src, real: isReal,
      /* always false now — an approximation is no longer renderable, so there is no
         third state between "Layne's block" and "a placeholder naming it". Kept on the
         type because `EchPlacement` is read in several places and a silent shape change
         is worse than a field that is always false. */
      approx: false,
    };
    return m;
  });
}

/* ── THE ASSEMBLY STAMP (code review 2026-08-19 #4) ────────────────────────────
   `echRebuildKeepEdits` was 60 lines, three tests and the whole of audit row 20 —
   and a spy measured ZERO calls, on a fresh assembly and on a template switch
   alike. The reason: StudioView decided "fresh vs rebuild" from a `useRef` created
   at mount, which is null exactly when a rebuild would be right, so the fresh
   branch always won. Row 20 was a false green.

   The fix is to record what the DOCUMENT was assembled from, on the document. That
   survives a Studio close/reopen — which is when the repo's second `/sfmc-blocks`
   pull actually lands, since the fetch effect runs per Studio mount while the block
   registry is module-scope. Before this, reopening the Studio re-assembled from
   block defaults and silently threw away every ECH edit; now the same template on
   fresher markup carries them, and the same template on the SAME markup is left
   completely alone. */
export function echStamp(doc: AnyBlockInstance[], tpl: string, ver: number): AnyBlockInstance[] {
  for (const b of doc || []) if (b && b.__ech) { b.__ech.tpl = tpl; b.__ech.ver = ver; }
  return doc;
}
/** what the current document was assembled from, or null if it is not an assembly */
export function echAsmOf(doc: AnyBlockInstance[] | null | undefined): { tpl: string; ver: number } | null {
  for (const b of doc || []) {
    if (b && b.__ech && b.__ech.tpl) return { tpl: b.__ech.tpl, ver: Number(b.__ech.ver || 0) };
  }
  return null;
}

/**
 * THE CAP FOR A BLOCK, AS MARKETING CLOUD DECLARES IT.
 *
 * Reads `maxCount` / `maxCombinedCount` off the active template's authored rules.
 * `maxCombinedCount` is why this takes a LIST of block names rather than one:
 * `featured-max-5` caps ECH_Featured1CTA and ECH_Featured2CTA together — five
 * across both variants, not five of each — so asking about either name must give
 * the same answer.
 *
 * Returns `null` when the template declares nothing, so the caller keeps its own
 * default rather than falling to zero and locking the section. A missing rule
 * means "no opinion", never "none allowed".
 */
export function echRuleMax(tpl: string | null | undefined, blocks: string[]): number | null {
  /* reads through `echTplRules`, which #41 already made the one way into this array —
     alias resolution included, so a doc stamped with "Tease - Coming Soon" answers the
     same as one stamped "Coming Soon". The 08-22 original walked ECH_TPLMETA directly
     and therefore missed aliases. */
  const rules = echTplRules(tpl || "");
  if (!rules.length) return null;
  const want = blocks.map((b) => String(b || "").toLowerCase()).filter(Boolean);
  if (!want.length) return null;
  for (const r of rules) {
    const t = String(r.type || "");
    if (t !== "maxCount" && t !== "maxCombinedCount") continue;
    const n = Number(r.max);
    if (!Number.isFinite(n) || n < 1) continue;
    const applies = (r.applies || []).map((a) => String(a || "").toLowerCase());
    if (applies.some((a) => want.indexOf(a) > -1)) return n;
  }
  return null;
}

/** the cap for the featured-community section of the doc's own template */
export function echFeaturedMax(doc: AnyBlockInstance[] | null | undefined, fallback: number): number {
  const asm = echAsmOf(doc);
  const n = echRuleMax(asm && asm.tpl, ["ECH_Featured1CTA", "ECH_Featured2CTA"]);
  return n == null ? fallback : n;
}

/** what a rebuild produced */
export interface EchRebuild {
  doc: AnyBlockInstance[];
  /** the id of the SAME block the user had selected, in the new document */
  sel: string | null;
  /** how many blocks carried edits forward */
  carried: number;
}

/**
 * A LATE BLOCK PULL MUST NOT WIPE THE USER'S WORK (monolith
 * `__echRebuildKeepEdits` L11748–11775 — audit row 20. 2026-08-19, per Jack:
 * "does a weird thing where I select an image or something then resets to the
 * default").
 *
 * Two async paths rebuild the document on fresher SFMC markup — the file:// block
 * cache loading, and the live `/sfmc-blocks` pull. Both did `doc = echDoc(curTpl)`,
 * a full rebuild from block DEFAULTS: whatever the user had picked or typed was
 * gone, and the selection with it. That is exactly the symptom — the photo snaps
 * back to the 600×350 placeholder and the Inspector reads NOTHING SELECTED.
 * Whether it fires mid-edit is pure timing; a slow Bynder pick is long enough for
 * the pull to land underneath it.
 *
 * The rebuild is still right (the markup IS fresher); throwing the data away was
 * not. So every field is carried forward, matched on **SFMC content id plus
 * occurrence index** — a template that uses the same block twice (Coming Soon's
 * two Section Bars) has to keep them straight, which an id-only match cannot do.
 * `__html` is the ONE key deliberately not carried: refreshing it is the entire
 * reason to rebuild. Selection is re-pointed at the same block's new id.
 *
 * Returns null when `name` is not an ECH template, or resolves to no blocks — in
 * which case the caller must leave the document alone.
 */
export function echRebuildKeepEdits(
  name: string,
  doc: AnyBlockInstance[] | null | undefined,
  sel: string | null,
  mk: (type: string) => AnyBlockInstance,
): EchRebuild | null {
  try {
    if (!echTplFor(name)) return null;
    /* ── THE USER'S STRUCTURE OUTRANKS THE TEMPLATE'S (2026-08-21) ────────────────
       Found by the layout-toggle double check: this used to rebuild from
       `echDoc(name)` — the TEMPLATE's declared block list — and carry fields onto
       it. Correct for fields, silently destructive for STRUCTURE: a section
       swapped to the 07 Listing Table, a card added with "Add plan", or a deleted
       block all reverted to the template the moment a fresher pull landed. So a
       document that already carries ECH blocks keeps ITS OWN block sequence, and
       the rebuild refreshes each block's markup (`__html`) from the re-registered
       definition — which is the entire reason to rebuild. Ids are untouched, so
       the selection survives as-is. The template-sequence path below still serves
       first assembly and the legacy-starter case, where there is no user
       structure to keep. */
    const hasEch = (doc || []).some((b) => b && (!!echMeta(b.type) || b.type === "echblock"));
    if (hasEch) {
      let carried = 0;
      const out = (doc || []).map((b) => {
        const d2 = FULL_REGISTRY[b.type];
        if (!d2 || !echMeta(b.type)) return b;
        carried++;
        let fresh = (d2.def() as Record<string, unknown>).__html;
        /* an ADDED collection lives in the markup — re-apply the recorded
           insertions so the refresh doesn't silently un-split the table */
        if (typeof fresh === "string" && Array.isArray((b.data || {})[EXTRA_COLLS])) {
          fresh = rowApplyExtraColls(fresh, b.data[EXTRA_COLLS]);
        }
        return typeof fresh === "string"
          ? { ...b, data: { ...b.data, __html: fresh } }
          : b;
      });
      return { doc: out, sel: sel ?? null, carried };
    }
    const nd = echDoc(name, mk);
    if (!nd || !nd.length) return null;
    const keyOf = (b: AnyBlockInstance, seen: Record<string, number>) => {
      const k = b && b.__ech && b.__ech.id ? "ech:" + b.__ech.id : "t:" + (b && b.type);
      seen[k] = (seen[k] || 0) + 1;
      return k + "#" + seen[k];
    };
    const prev: Record<string, AnyBlockInstance> = {};
    const s1: Record<string, number> = {};
    (doc || []).forEach((b) => { prev[keyOf(b, s1)] = b; });
    let selBlock: AnyBlockInstance | null = null;
    (doc || []).forEach((b) => { if (b && b.id === sel) selBlock = b; });
    let newSel: string | null = null, carried = 0;
    const s2: Record<string, number> = {};
    nd.forEach((b) => {
      const p = prev[keyOf(b, s2)];
      if (!p) return;
      Object.keys(p.data || {}).forEach((k) => {
        if (k === "__html") return;
        b.data[k] = p.data[k];
      });
      carried++;
      if (p === selBlock) newSel = b.id;
    });
    return { doc: nd, sel: newSel, carried };
  } catch { return null; }
}

/** test seam only: drop every loaded template */
export function __resetEchTemplates(): void {
  const clr = (o: Record<string, unknown>) => Object.keys(o).forEach((k) => { delete o[k]; });
  clr(ECH_MAP); clr(ECH_ALIAS); clr(ECH_TPLMETA);
  TPL_VERSION = 0;
}
